import { memo } from 'react';
import { AccessAuthorities } from '@/models';

// ── Types (shared with PermissionsScreen) ────────────────────────────

interface PermissionGroup {
  id: number | null;
  title: string;
  type?: string;
  permissions: string[];
  requiredRoles: string[];
  allowedRoles: string[];
}

interface PermissionCategory {
  id: number;
  name: string;
  permission: string;
  children: PermissionRow[];
}

interface PermissionRow {
  id: number;
  name: string;
  permission: string;
  parentId: number | null;
}

interface ChildRow {
  id: number;
  name: string;
  permission: string;
  parentId: number | null;
  indent: number;
}

// ── Helper Functions ─────────────────────────────────────────────────

function getGroupKey(groupId: number | null): string {
  return groupId === null ? 'new' : String(groupId);
}

function isCustomGroup(group: PermissionGroup): boolean {
  return group.requiredRoles.length === 0;
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function highlightText(text: string, searchTerm: string): string {
  if (!searchTerm) return escapeHtml(text);
  const escaped = escapeHtml(text);
  const regex = new RegExp('(' + escapeRegex(searchTerm) + ')', 'gi');
  return escaped.replace(regex, '<mark>$1</mark>');
}

function getTooltip(permission: string): string {
  if (permission === AccessAuthorities.ROLE_CUSTOM_LOCATION_VIEW_SECTION) {
    return 'View the Custom Locations section using the Custom Locations menu item at the top.';
  }
  if (permission === AccessAuthorities.ROLE_CUSTOM_LOCATION_SAVE) {
    return 'Save Custom Locations from the Custom Locations section, Calls for Service, or the Map.';
  }
  if (permission === AccessAuthorities.ROLE_CUSTOM_LOCATION_UPDATE) {
    return 'Update Custom Locations from the Custom Locations section.';
  }
  if (permission === AccessAuthorities.ROLE_CUSTOM_LOCATION_DELETE) {
    return 'Delete Custom Locations from the Custom Locations section.';
  }
  if (permission === AccessAuthorities.ROLE_CUSTOM_LOCATION_AUDIT_LOG) {
    return 'View Custom Locations Audit Log from the Custom Locations section.';
  }
  if (permission === AccessAuthorities.ROLE_DISPATCH_OFFICER_STATUS) {
    return 'Allows updating the status of users who are in the "MDT Group" permission group.';
  }
  if (permission === AccessAuthorities.ROLE_DISPATCH_ANY_STATUS) {
    return 'Allows updating the status of any user in the company, regardless of their permission group.';
  }
  return '';
}

function getAllPermissionsInCategory(category: PermissionCategory): string[] {
  const perms: string[] = [];
  if (category.permission) {
    perms.push(category.permission);
  }
  for (let i = 0; i < category.children.length; i++) {
    if (category.children[i].permission) {
      perms.push(category.children[i].permission);
    }
  }
  return perms;
}

function getCategoryCounts(
  category: PermissionCategory,
  group: PermissionGroup,
  permissionStore: { [groupId: string]: { [permission: string]: boolean } }
): { enabled: number; total: number; allowed: number } {
  const allPerms = getAllPermissionsInCategory(category);
  let enabledCount = 0;
  let allowedCount = 0;
  const groupKey = getGroupKey(group.id);
  const groupPerms = permissionStore[groupKey] || {};

  for (let i = 0; i < allPerms.length; i++) {
    const perm = allPerms[i];
    if (groupPerms[perm] || group.requiredRoles.indexOf(perm) > -1) {
      enabledCount++;
    }
    if (group.allowedRoles.indexOf(perm) > -1 || group.requiredRoles.indexOf(perm) > -1) {
      allowedCount++;
    }
  }

  return { enabled: enabledCount, total: allPerms.length, allowed: allowedCount };
}

function getCategoryState(
  category: PermissionCategory,
  group: PermissionGroup,
  permissionStore: { [groupId: string]: { [permission: string]: boolean } }
): string {
  const counts = getCategoryCounts(category, group, permissionStore);
  if (counts.allowed === 0) return 'unavailable';
  if (counts.enabled === counts.total) return 'all';
  if (counts.enabled > 0) return 'some';
  return 'none';
}

function getCellState(
  permission: string,
  group: PermissionGroup,
  permissionStore: { [groupId: string]: { [permission: string]: boolean } }
): string {
  if (!permission) return 'header';
  if (group.requiredRoles.indexOf(permission) > -1) return 'required';
  const groupKey = getGroupKey(group.id);
  const groupPerms = permissionStore[groupKey];
  if (groupPerms && groupPerms[permission]) return 'enabled';
  if (group.allowedRoles.indexOf(permission) > -1) return 'allowed';
  return 'unavailable';
}

// ── CategoryRows sub-component ───────────────────────────────────────

interface CategoryRowsProps {
  category: PermissionCategory;
  childRows: ChildRow[];
  expanded: boolean;
  visibleGroups: PermissionGroup[];
  permissionStore: { [groupId: string]: { [permission: string]: boolean } };
  searchTerm: string;
  onToggleCategory: (categoryId: number, event: React.MouseEvent) => void;
  onToggleCategoryPermissions: (categoryId: number, groupId: number | null) => void;
  onTogglePermission: (groupId: number | null, permission: string, shouldEnable: boolean) => void;
  isSearchMatch: (name: string) => boolean;
}

function CategoryRows({
  category,
  childRows,
  expanded,
  visibleGroups,
  permissionStore,
  searchTerm,
  onToggleCategory,
  onToggleCategoryPermissions,
  onTogglePermission,
  isSearchMatch,
}: CategoryRowsProps) {
  return (
    <>
      {/* Category row */}
      <tr
        className={'category-row' + (expanded ? ' category-expanded' : '')}
        data-category-id={String(category.id)}
        onClick={(e) => onToggleCategory(category.id, e)}
      >
        <td className="category-name">
          <i className={'fa ' + (expanded ? 'fa-chevron-down' : 'fa-chevron-right')}></i>
          <span dangerouslySetInnerHTML={{ __html: highlightText(category.name, searchTerm) }} />
          <span className="category-count">({childRows.length})</span>
        </td>
        {visibleGroups.map((group) => {
          const state = getCategoryState(category, group, permissionStore);
          const counts = getCategoryCounts(category, group, permissionStore);

          return (
            <td
              key={'catcell-' + category.id + '-' + getGroupKey(group.id)}
              className={'permission-cell category-cell category-state-' + state}
              data-category-id={String(category.id)}
              data-group-id={String(group.id)}
              onClick={(e) => {
                e.stopPropagation();
                onToggleCategoryPermissions(category.id, group.id);
              }}
            >
              {(state === 'all' || state === 'none') && (
                <input
                  type="checkbox"
                  className="orna-checkbox category-checkbox-input cat-checkbox"
                  checked={state === 'all'}
                  data-category-id={String(category.id)}
                  data-group-id={String(group.id)}
                  onChange={() => {}} // controlled by cell click
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleCategoryPermissions(category.id, group.id);
                  }}
                />
              )}
              {state === 'some' && (
                <span
                  className="category-partial-count cat-partial"
                  data-category-id={String(category.id)}
                  data-group-id={String(group.id)}
                  title="Click to select all or none"
                >
                  {counts.enabled}/{counts.total}
                </span>
              )}
              {state === 'unavailable' && <span className="cell-unavailable">-</span>}
            </td>
          );
        })}
      </tr>

      {/* Permission rows (expanded) */}
      {expanded &&
        childRows.map((row) => {
          const tooltip = getTooltip(row.permission);
          return (
            <tr
              key={'row-' + category.id + '-' + row.id}
              className={'permission-row' + (isSearchMatch(row.name) ? ' search-match' : '')}
              data-category-id={String(category.id)}
            >
              <td
                className="permission-name"
                style={{ paddingLeft: row.indent * 20 + 30 + 'px' }}
              >
                {row.indent === 2 && '- '}
                <span dangerouslySetInnerHTML={{ __html: highlightText(row.name, searchTerm) }} />
                {tooltip && (
                  <span className="permission-tooltip">
                    <i className="fa fa-question-circle"></i>
                    <span className="tooltip-text">{tooltip}</span>
                  </span>
                )}
              </td>
              {visibleGroups.map((group) => {
                const cellState = getCellState(row.permission, group, permissionStore);

                return (
                  <td
                    key={'permcell-' + row.id + '-' + getGroupKey(group.id)}
                    className={'permission-cell ' + cellState}
                  >
                    {cellState === 'required' && (
                      <span className="cell-icon required">
                        <i className="fa fa-check"></i>
                      </span>
                    )}
                    {(cellState === 'enabled' || cellState === 'allowed') && (
                      <input
                        type="checkbox"
                        className="orna-checkbox perm-checkbox"
                        checked={cellState === 'enabled'}
                        data-group-id={String(group.id)}
                        data-permission={row.permission}
                        onChange={() => {
                          onTogglePermission(group.id, row.permission, cellState !== 'enabled');
                        }}
                      />
                    )}
                    {cellState === 'unavailable' && <span className="cell-unavailable">-</span>}
                  </td>
                );
              })}
            </tr>
          );
        })}
    </>
  );
}

// ── PermissionsGroupsTab ─────────────────────────────────────────────

interface PermissionGroupDisplay extends PermissionGroup {}

interface PermissionsGroupsTabProps {
  searchTerm: string;
  permissionGroups: PermissionGroup[];
  visibleGroups: PermissionGroupDisplay[];
  filteredCategories: PermissionCategory[];
  permissionStore: { [groupId: string]: { [permission: string]: boolean } };
  editingGroupId: number | null;
  groupTitleInputRef: React.RefObject<HTMLInputElement | null>;
  maxGroupTitleLength: number;
  onSearchTermChange: (value: string) => void;
  onClearSearch: () => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  isGroupVisible: (groupId: number | null) => boolean;
  onToggleGroupVisibility: (groupId: number | null) => void;
  onShowAllGroups: () => void;
  onHideAllGroups: () => void;
  getCategoryChildRows: (categoryId: number) => ChildRow[];
  isCategoryExpanded: (categoryId: number) => boolean;
  onToggleCategory: (categoryId: number, event: React.MouseEvent) => void;
  onToggleCategoryPermissions: (categoryId: number, groupId: number | null) => void;
  onTogglePermission: (groupId: number | null, permission: string, shouldEnable: boolean) => void;
  isSearchMatch: (name: string) => boolean;
  getGroupDisplayTitle: (group: PermissionGroup) => string;
  onStartEditingGroupTitle: (groupId: number) => void;
  onSaveGroupTitle: (groupId: number, event: React.KeyboardEvent | React.MouseEvent) => void;
  onCancelEditingGroupTitle: () => void;
  onOpenDeleteModal: (groupId: number) => void;
}

export const PermissionsGroupsTab = memo(function PermissionsGroupsTab({
  searchTerm,
  permissionGroups,
  visibleGroups,
  filteredCategories,
  permissionStore,
  editingGroupId,
  groupTitleInputRef,
  maxGroupTitleLength,
  onSearchTermChange,
  onClearSearch,
  onExpandAll,
  onCollapseAll,
  isGroupVisible,
  onToggleGroupVisibility,
  onShowAllGroups,
  onHideAllGroups,
  getCategoryChildRows,
  isCategoryExpanded,
  onToggleCategory,
  onToggleCategoryPermissions,
  onTogglePermission,
  isSearchMatch,
  getGroupDisplayTitle,
  onStartEditingGroupTitle,
  onSaveGroupTitle,
  onCancelEditingGroupTitle,
  onOpenDeleteModal,
}: PermissionsGroupsTabProps) {
  return (
    <div className="permissions-content">
      {/* Controls */}
      <div className="permissions-controls">
        <div className="search-box">
          <i className="fa fa-search"></i>
          <input
            type="text"
            id="searchInput"
            className="search-input"
            placeholder="Search permissions..."
            value={searchTerm}
            onChange={(e) => onSearchTermChange(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear-search" id="btnClearSearch" onClick={onClearSearch}>
              <i className="fa fa-times"></i>
            </button>
          )}
        </div>
        <div className="expand-controls">
          <button className="btn btn-default btn-sm" id="btnExpandAll" onClick={onExpandAll}>
            <i className="fa fa-expand"></i> <span className="btn-text">Expand All</span>
          </button>
          <button className="btn btn-default btn-sm" id="btnCollapseAll" onClick={onCollapseAll}>
            <i className="fa fa-compress"></i> <span className="btn-text">Collapse All</span>
          </button>
        </div>
      </div>

      {/* Column Visibility */}
      <div className="column-visibility">
        <span className="visibility-label">Show Groups:</span>
        <div className="visibility-checkboxes">
          {permissionGroups.map((group) => (
            <label key={'vis-' + getGroupKey(group.id)} className="visibility-checkbox">
              <input
                type="checkbox"
                className="orna-checkbox"
                data-group-visibility={String(group.id)}
                checked={isGroupVisible(group.id)}
                onChange={() => onToggleGroupVisibility(group.id)}
              />
              {' ' + group.title}
            </label>
          ))}
        </div>
        <div className="visibility-actions">
          <button className="btn btn-link btn-sm" id="btnShowAllGroups" onClick={onShowAllGroups}>
            Show All
          </button>
          <button className="btn btn-link btn-sm" id="btnHideAllGroups" onClick={onHideAllGroups}>
            Hide All
          </button>
        </div>
      </div>

      {/* Matrix */}
      <div id="permissionsMatrixContainer">
        {visibleGroups.length > 0 ? (
          <div className="permissions-matrix">
            <table className="permissions-table">
              <thead>
                <tr className="sticky-header">
                  <th className="permission-name-header">Permission</th>
                  {visibleGroups.map((group) => {
                    const gKey = getGroupKey(group.id);
                    const isCustom = isCustomGroup(group);
                    const isEditing = editingGroupId === group.id;
                    const displayTitle = getGroupDisplayTitle(group);

                    if (isCustom && isEditing) {
                      return (
                        <th
                          key={'hdr-' + gKey}
                          className="group-header group-header-custom group-header-editing"
                        >
                          <input
                            type="text"
                            className="group-title-input"
                            data-group-id={String(group.id)}
                            defaultValue={displayTitle}
                            maxLength={maxGroupTitleLength}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') onSaveGroupTitle(group.id as number, e);
                              if (e.key === 'Escape') onCancelEditingGroupTitle();
                            }}
                            ref={groupTitleInputRef}
                          />
                          <div className="edit-actions">
                            <button
                              className="btn-save-title"
                              data-group-id={String(group.id)}
                              title="Save"
                              onClick={(e) => onSaveGroupTitle(group.id as number, e)}
                            >
                              <i className="fa fa-check"></i>
                            </button>
                            <button
                              className="btn-cancel-edit"
                              data-group-id={String(group.id)}
                              title="Cancel"
                              onClick={onCancelEditingGroupTitle}
                            >
                              <i className="fa fa-times"></i>
                            </button>
                          </div>
                        </th>
                      );
                    } else if (isCustom) {
                      return (
                        <th key={'hdr-' + gKey} className="group-header group-header-custom">
                          <span
                            className="group-title"
                            data-group-id={String(group.id)}
                            title="Click to edit"
                            onClick={() => onStartEditingGroupTitle(group.id as number)}
                          >
                            {displayTitle}
                          </span>
                          <div className="group-header-actions">
                            <button
                              className="btn-edit-group"
                              data-group-id={String(group.id)}
                              title="Edit name"
                              onClick={() => onStartEditingGroupTitle(group.id as number)}
                            >
                              <i className="fa fa-pencil"></i>
                            </button>
                            <button
                              className="btn-delete-group"
                              data-group-id={String(group.id)}
                              title="Delete group"
                              onClick={() => onOpenDeleteModal(group.id as number)}
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          </div>
                        </th>
                      );
                    } else {
                      return (
                        <th key={'hdr-' + gKey} className="group-header">
                          {group.title}
                        </th>
                      );
                    }
                  })}
                </tr>
              </thead>
              <tbody>
                {filteredCategories.map((category) => {
                  const childRows = getCategoryChildRows(category.id);
                  const expanded = isCategoryExpanded(category.id);

                  return (
                    <CategoryRows
                      key={'cat-' + category.id}
                      category={category}
                      childRows={childRows}
                      expanded={expanded}
                      visibleGroups={visibleGroups}
                      permissionStore={permissionStore}
                      searchTerm={searchTerm}
                      onToggleCategory={onToggleCategory}
                      onToggleCategoryPermissions={onToggleCategoryPermissions}
                      onTogglePermission={onTogglePermission}
                      isSearchMatch={isSearchMatch}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="no-groups-message">
            <p>No permission groups selected. Use the checkboxes above to show groups.</p>
          </div>
        )}
      </div>
    </div>
  );
});
